import React from 'react';
import { Box, Typography, Button, IconButton } from '@weave-mui/material';
import { TrashM } from '@weave-mui/icons-weave';
import { List, ListItem } from '@mui/material';

/**
 * SelectedTestsList - Displays the list of selected test cases in the right panel
 *
 * Shows an empty state when no tests are selected, or a list of selected tests
 * with options to remove individual tests or clear all.
 *
 * @param {Object} props - Component props
 * @param {Array} [props.testCases=[]] - Array of selected test case objects
 * @param {Function} [props.onRemove=() => {}] - Callback when removing a single test case
 * @param {Function} [props.onContinue=() => {}] - Callback when clicking "Continue to Review"
 * @param {Function} [props.onClearAll=() => {}] - Callback when clicking "Clear all"
 * @returns {JSX.Element} The selected tests list component
 */
function SelectedTestsList({
  testCases = [],
  onRemove = () => {},
  onContinue = () => {},
  onClearAll = () => {},
}) {
  const hasTests = Array.isArray(testCases) && testCases.length > 0;

  const content = hasTests ? (
    <List sx={{ mb: 3, px: 0 }}>
      {testCases.map((testCase) => (
        <ListItem
          key={testCase.testcaseid}
          disableGutters
          sx={{
            mb: 1,
            px: 2,
            py: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: (theme) => theme.palette.action.hover,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
          secondaryAction={
            <IconButton
              edge="end"
              onClick={() => onRemove(testCase)}
              size="small"
            >
              <TrashM color="action" />
            </IconButton>
          }
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 1,
              width: '100%',
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {testCase.subcategoryname}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
            >
              {testCase.displayname || testCase.name}
            </Typography>
          </Box>
        </ListItem>
      ))}
    </List>
  ) : (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 8,
        px: 4,
        color: 'text.secondary',
        minHeight: 200,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
        Your test suite is empty
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Drag or click on test cases from a category on the left to add and build
        your test suite.
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        Test suite
      </Typography>

      {/* Clear all button */}
      {hasTests && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="outlined" size="small" onClick={onClearAll}>
            Clear all
          </Button>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: '550px' }}>
        {content}
      </Box>

      {/* Continue button */}
      {hasTests && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 'auto',
            pt: 2,
          }}
        >
          <Button variant="contained" onClick={onContinue} sx={{ height: 40 }}>
            Continue to Review
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default SelectedTestsList;
